import React, { useEffect, useMemo } from "react";
import { Loading } from "../components/Loader";
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import {
  fetchInventoryRevisionDetail,
  fetchInventoryRevisionItems,
  selectItemsByRevisionId,
  selectRevisionById,
  selectRevisionErrorById,
  selectRevisionStatusById,
} from "../features/inventoryRevisions/inventoryRevisionsSlice";
import {
  computePostedRevisionFinance,
  formatRevisionQty,
  getPostedRevisionFinanceRows,
  moneyRu,
} from "../shared/inventoryRevisionPostedFinance";

const styles = {
  page: {
    minHeight: "100%",
    padding: "18px 18px 28px",
    background: "radial-gradient(1200px 600px at 20% -10%, rgba(245,197,24,0.08), transparent 60%), var(--c-bg)",
    color: "var(--c-text)",
  },
  top: { marginBottom: 16 },
  breadcrumbs: { display: "flex", alignItems: "center", gap: 8, fontSize: 14, opacity: 0.9, flexWrap: "wrap" },
  link: { color: "var(--c-s1)", textDecoration: "none", fontWeight: 900 },
  sep: { opacity: 0.5 },
  title: { fontSize: 22, fontWeight: 900, marginTop: 10 },
  sub: { marginTop: 8, opacity: 0.8, fontSize: 15, maxWidth: 900, lineHeight: 1.45 },
  notice: {
    marginTop: 12,
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid rgba(245,197,24,0.35)",
    background: "rgba(245,197,24,0.08)",
    color: "var(--c-text)",
    fontSize: 15,
    fontWeight: 800,
    maxWidth: 900,
  },
  totals: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
    gap: 12,
    marginTop: 18,
    marginBottom: 18,
  },
  totalCard: {
    padding: "14px 16px",
    borderRadius: 16,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
  },
  totalLabel: { fontSize: 14, fontWeight: 800, color: "var(--c-text-sub)", marginBottom: 6 },
  totalValue: { fontSize: 20, fontWeight: 900 },
  tableWrap: {
    border: "1px solid var(--c-border)",
    background: "var(--c-input)",
    borderRadius: 16,
    overflow: "auto",
  },
  table: { width: "100%", borderCollapse: "separate", borderSpacing: 0, minWidth: 720 },
  th: {
    textAlign: "left",
    padding: "10px 12px",
    fontSize: 14,
    fontWeight: 900,
    color: "var(--c-text-muted)",
    background: "var(--c-input)",
    borderBottom: "1px solid var(--c-border)",
    whiteSpace: "nowrap",
  },
  td: {
    padding: "10px 12px",
    fontSize: 15,
    borderBottom: "1px solid var(--c-border)",
    verticalAlign: "middle",
  },
  tdMono: {
    padding: "10px 12px",
    fontSize: 15,
    borderBottom: "1px solid var(--c-border)",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
  },
  tdMuted: { padding: 14, fontSize: 15, opacity: 0.75 },
  error: { color: "var(--c-danger)", fontWeight: 800, marginTop: 12 },
};

const KIND_META = {
  shortage: {
    title: "Недостача",
    hint: "Ожидалось больше, чем по факту (ожид. − факт > 0). Суммы: по себестоимости (self cost), по розничной цене карточки товара и по опту.",
    rowsKey: "shortage",
    totalsKey: "tShort",
  },
  surplus: {
    title: "Излишек",
    hint: "По факту больше, чем ожидалось (ожид. − факт < 0). Суммы: по себестоимости, по рознице и по опту.",
    rowsKey: "surplus",
    totalsKey: "tSur",
  },
};

export default function WarehouseRevisionFinanceKindPage() {
  const { revisionId, kind } = useParams();
  const dispatch = useDispatch();
  const revision = useSelector(selectRevisionById(revisionId));
  const revStatus = useSelector(selectRevisionStatusById(revisionId));
  const revError = useSelector(selectRevisionErrorById(revisionId));
  const items = useSelector(selectItemsByRevisionId(revisionId));

  const mode = kind === "surplus" ? "surplus" : kind === "shortage" ? "shortage" : null;
  const meta = mode ? KIND_META[mode] : null;

  useEffect(() => {
    dispatch(fetchInventoryRevisionDetail({ revisionId }));
  }, [dispatch, revisionId]);

  useEffect(() => {
    if (revision?.status === "posted" && (!Array.isArray(revision.items) || revision.items.length === 0)) {
      dispatch(fetchInventoryRevisionItems({ revisionId, barcode: "", productName: "" }));
    }
  }, [dispatch, revision, revisionId]);

  const financeSource = useMemo(() => getPostedRevisionFinanceRows(revision, items), [revision, items]);
  const finance = useMemo(() => {
    if (!financeSource?.rows?.length) return null;
    return computePostedRevisionFinance(financeSource.rows);
  }, [financeSource]);

  const scopeNote =
    financeSource?.scope === "list"
      ? "Данные без вложенного списка позиций в ответе ревизии: используется последняя загрузка строк. Для полного совпадения с Excel обновите страницу ревизии."
      : null;

  if (!mode || !meta) {
    return (
      <div style={styles.page}>
        <div style={styles.error}>Неизвестный раздел. Перейдите по ссылке с карточки ревизии.</div>
        <Link style={{ ...styles.link, marginTop: 12, display: "inline-block" }} to={`/warehouse/revision/${revisionId}`}>
          К ревизии
        </Link>
      </div>
    );
  }

  const rows = finance ? finance[meta.rowsKey] : [];
  const totals = finance ? finance[meta.totalsKey] : null;
  const base = `/warehouse/revision/${revisionId}`;

  return (
    <div style={styles.page}>
      <div style={styles.top}>
        <div style={styles.breadcrumbs}>
          <Link style={styles.link} to="/warehouse/revision">
            Ревизии
          </Link>
          <span style={styles.sep}>/</span>
          <Link style={styles.link} to={base}>
            Ревизия #{revisionId}
          </Link>
          <span style={styles.sep}>/</span>
          <span style={{ fontWeight: 900 }}>{meta.title}</span>
        </div>
        <h1 style={styles.title}>{meta.title}</h1>
        <p style={styles.sub}>{meta.hint}</p>
        {scopeNote && <div style={styles.notice}>{scopeNote}</div>}
      </div>

      {revStatus === "loading" && <Loading inline text="Загрузка…" />}
      {revStatus === "failed" && <div style={styles.error}>{revError}</div>}

      {revision && revision.status !== "posted" && (
        <div style={styles.error}>Финансовый раздел доступен только для опубликованных ревизий.</div>
      )}

      {revision?.status === "posted" && !finance && <div style={styles.sub}>Нет строк позиций для расчёта.</div>}

      {revision?.status === "posted" && finance && totals && (
        <>
          <div style={styles.totals}>
            <div style={styles.totalCard}>
              <div style={styles.totalLabel}>Итого по себестоимости ({meta.title.toLowerCase()})</div>
              <div style={styles.totalValue}>{moneyRu(totals.cost)}</div>
            </div>
            <div style={styles.totalCard}>
              <div style={styles.totalLabel}>Итого по розничной цене ({meta.title.toLowerCase()})</div>
              <div style={styles.totalValue}>{moneyRu(totals.retail)}</div>
            </div>
            <div style={styles.totalCard}>
              <div style={styles.totalLabel}>Итого по опту ({meta.title.toLowerCase()})</div>
              <div style={styles.totalValue}>{moneyRu(totals.wholesale)}</div>
            </div>
            <div style={styles.totalCard}>
              <div style={styles.totalLabel}>Строк с отклонением</div>
              <div style={styles.totalValue}>{totals.lines}</div>
            </div>
            <div style={styles.totalCard}>
              <div style={styles.totalLabel}>Сумма количества (ед.)</div>
              <div style={styles.totalValue}>{formatRevisionQty(totals.qty)}</div>
            </div>
          </div>

          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Товар</th>
                  <th style={styles.th}>Штрихкод</th>
                  <th style={styles.th}>Кол-во</th>
                  <th style={styles.th}>Себестоимость</th>
                  <th style={styles.th}>Розница</th>
                  <th style={styles.th}>Опт</th>
                  <th style={styles.th}>Опт − себест.</th>
                  <th style={styles.th}>Розн. − себест.</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={styles.tdMuted}>
                      Нет строк в этой категории
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <tr key={row.key}>
                      <td style={styles.td}>{row.product_name}</td>
                      <td style={styles.tdMono}>{row.barcode || "—"}</td>
                      <td style={styles.td}>{formatRevisionQty(row.qty)}</td>
                      <td style={styles.td}>{moneyRu(row.amount_cost)}</td>
                      <td style={styles.td}>{moneyRu(row.amount_retail)}</td>
                      <td style={styles.td}>{moneyRu(row.amount_wholesale)}</td>
                      <td style={styles.td}>{moneyRu(row.diff_wh_minus_cost)}</td>
                      <td style={styles.td}>{moneyRu(row.diff_retail_minus_cost)}</td>
                    </tr>
                  ))
                )}
                {rows.length > 0 && (
                  <tr>
                    <td style={{ ...styles.td, fontWeight: 900 }} colSpan={2}>
                      Итого
                    </td>
                    <td style={{ ...styles.td, fontWeight: 900 }}>{formatRevisionQty(totals.qty)}</td>
                    <td style={{ ...styles.td, fontWeight: 900 }}>{moneyRu(totals.cost)}</td>
                    <td style={{ ...styles.td, fontWeight: 900 }}>{moneyRu(totals.retail)}</td>
                    <td style={{ ...styles.td, fontWeight: 900 }}>{moneyRu(totals.wholesale)}</td>
                    <td style={{ ...styles.td, fontWeight: 900 }}>{moneyRu(totals.wholesale - totals.cost)}</td>
                    <td style={{ ...styles.td, fontWeight: 900 }}>{moneyRu(totals.retail - totals.cost)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: 18, display: "flex", gap: 12, flexWrap: "wrap" }}>
            <Link style={styles.link} to={base}>
              ← К ревизии
            </Link>
            {mode === "shortage" ? (
              <Link style={styles.link} to={`${base}/finance/surplus`}>
                Раздел «Излишек» →
              </Link>
            ) : (
              <Link style={styles.link} to={`${base}/finance/shortage`}>
                ← Раздел «Недостача»
              </Link>
            )}
          </div>
        </>
      )}
    </div>
  );
}
